import { useState, useEffect, useRef, useCallback } from "react";
import { IoWarningOutline } from "react-icons/io5";
import { FaRegEdit } from "react-icons/fa";
import { BsBuilding } from "react-icons/bs";
import { GoTrash } from "react-icons/go";
import { LuCircleUser } from "react-icons/lu";
import { strings } from "../../../strings/strings";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import { apiCall } from "../../../api/fetchClient";
import { GET_DASHBOARD_ACTIVITIES_URL } from "../../../api/url";

const RecentActivity = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef(null);
  const loadingRef = useRef(null);
  const hasFetchedInitial = useRef(false);
  const isFetchingRef = useRef(false);

  const fetchActivities = useCallback(async (pageNum) => {
    if (isFetchingRef.current || !hasMore) return;

    try {
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);
      const response = await apiCall(`${GET_DASHBOARD_ACTIVITIES_URL}?page=${pageNum}&limit=10`, "GET");
      const newActivities = response?.results?.activities || [];
      const pagination = response?.results?.pagination || {};

      setActivities((prev) => [...prev, ...newActivities]);
      setHasMore(pagination.hasNextPage || false);
      setLoading(false);
      isFetchingRef.current = false;
    } catch (error) {
      setError(error?.message);
      setLoading(false);
      isFetchingRef.current = false;
      setHasMore(false);
    }
  }, [hasMore]);

  useEffect(() => {
    if (page === 1 && hasFetchedInitial.current) return;
    if (page === 1) hasFetchedInitial.current = true;

    fetchActivities(page);
  }, [page, fetchActivities]);

  useEffect(() => {
    if (!hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingRef.current) {
          setPage((prev) => prev + 1);
        }
      },
      {
        threshold: 0.1,
        root: null,
        rootMargin: "0px",
      },
    );

    const currentLoadingRef = loadingRef.current;
    if (currentLoadingRef) {
      observer.observe(currentLoadingRef);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current && currentLoadingRef) {
        observerRef.current.unobserve(currentLoadingRef);
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading]);

  const getActivityIcon = (type) => {
    switch (type) {
      case "company_created":
      case "login":
        return <LuCircleUser className="w-4 h-4" />;
      case "profile_updated":
      case "company_updated":
      case "company_status_changed":
        return <FaRegEdit className="w-4 h-4" />;
      case "company_deleted":
        return <GoTrash className="w-4 h-4" />;
      case "company_suspended":
        return <IoWarningOutline className="w-4 h-4" />;
      default:
        return <BsBuilding className="w-4 h-4" />;
    }
  };

  const getIconBgColor = (type) => {
    switch (type) {
      case "company_created":
      case "login":
        return "bg-primary dark:bg-primary-dark";
      case "profile_updated":
      case "company_updated":
      case "company_status_changed":
        return "bg-blue-500 dark:bg-blue-600";
      case "company_deleted":
        return "bg-gray-500 dark:bg-gray-600";
      case "company_suspended":
        return "bg-red-500 dark:bg-red-600";
      default:
        return "bg-gray-500 dark:bg-gray-600";
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? "s" : ""} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between mb-6 flex-shrink-0">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">{strings.RECENT_ACTIVITY_TITLE}</h2>
      </div>
      <div
        className={`space-y-2 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent flex-1 min-h-0
    ${activities.length === 0 ? "overflow-hidden" : ""}`}
      >
        {activities.length === 0 && !loading && !error && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">No recent activities</div>
        )}
        {activities.length === 0 && !loading && error && (
          <div className="text-center py-8 text-red-500 dark:text-red-400">
            {error}
          </div>
        )}
        {activities.length > 0 && (
          activities.map((activity, index) => (
            <div
              key={`${activity.companyName}-${activity.createdAt}-${index}`}
              className="flex items-center gap-3 p-3 rounded-lg bg-gray-100 dark:bg-gray-700/50 transition-colors"
            >
              <div
                className={`flex-shrink-0 w-7 h-7 ${getIconBgColor(
                  activity.activityType,
                )} rounded-lg flex items-center justify-center text-white`}
              >
                {getActivityIcon(activity.activityType)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between text-sm text-gray-900 dark:text-white">
                  <div className="flex gap-2">
                    <span className="font-semibold">{activity.companyName}</span>
                    <span className="text-gray-600 dark:text-gray-400">
                      <p className="flex gap-2">
                        <span>{activity.targetName}</span>
                        <span> {activity.description}</span>
                      </p>
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-500 mt-1 whitespace-nowrap ml-2">
                    {formatTime(activity.createdAt)}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
        {loading && (
          <div className="flex justify-center py-4">
            <LoadingSpinner size="sm" minHeight="min-h-0" message="" />
          </div>
        )}
        {hasMore && !loading && <div ref={loadingRef} className="h-4" />}
      </div>
    </div>
  );
};

export default RecentActivity;
